/**
 * Controls where the header notification UI appears and which
 * module's notifications are loaded (Tasks vs Forum vs Documents).
 */

const normalizePath = (pathname = '') => {
  if (!pathname) return '/';
  const trimmed =
    pathname.endsWith('/') && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;
  return trimmed.toLowerCase();
};

/** Exact listing / module-dashboard paths that should show notifications. */
export const MODULE_NOTIFICATION_PAGES = {
  tasks: {
    tableName: 'Tasks',
    menuKeys: ['tasks'],
    menuTitles: ['Tasks'],
    exact: [
      '/task/tasklist',
      '/task/taskboard',
      '/task/worksheet',
      '/task/projectlist',
      '/task/performance',
      '/work-register/list',
      '/work-register/team',
    ],
    prefixes: [],
  },
  forum: {
    tableName: 'DiscussionForums',
    menuKeys: ['forum'],
    menuTitles: ['Discussion Forum'],
    exact: ['/forum/dashboard', '/forum/list'],
    prefixes: [],
  },
  documents: {
    tableName: 'Documents',
    menuKeys: ['documents'],
    menuTitles: ['Documents'],
    exact: [
      '/documents/dashboard',
      '/documents/mydocuments',
      '/documents/shared',
      '/documents/favorites',
      '/documents/documentclassifications',
    ],
    prefixes: ['/documents/folder/'],
  },
};

export const NOTIFICATIONS_UPDATED_EVENT = 'notifications-updated';
export const DROPDOWN_NOTIFICATION_MAX_AGE_DAYS = 10;

const TASK_TYPES = new Set([10, 20, 30, 40, 50, 60, 70, 80, 90, 100]);
const FORUM_TYPES = new Set([110, 111, 112, 113]);
const PROJECT_TYPES = new Set([120, 121]);
const DOCUMENT_TYPES = new Set([130]);

/**
 * @param {string} pathname
 * @returns {'tasks'|'forum'|'documents'|null}
 */
export function getNotificationModuleKey(pathname) {
  const path = normalizePath(pathname);

  for (const [key, config] of Object.entries(MODULE_NOTIFICATION_PAGES)) {
    if (config.exact.includes(path)) return key;
    if (config.prefixes.some((prefix) => path.startsWith(prefix))) return key;
  }

  return null;
}

/** Whether the notification bell / ticker should render on this page. */
export function shouldShowNotifications(pathname) {
  return getNotificationModuleKey(pathname) != null;
}

/** API `TableName` query for the active module (or null). */
export function getNotificationTableName(pathname) {
  const key = getNotificationModuleKey(pathname);
  if (!key) return null;
  return MODULE_NOTIFICATION_PAGES[key].tableName;
}

/**
 * Resolve notification module key for a sidebar MenuList parent item.
 * @param {{ menu_key?: string, title?: string }} menuItem
 * @returns {'tasks'|'forum'|'documents'|null}
 */
export function getNotificationModuleKeyForMenuItem(menuItem) {
  if (!menuItem) return null;
  const menuKey = (menuItem.menu_key || '').toLowerCase();
  const title = menuItem.title || '';

  for (const [key, config] of Object.entries(MODULE_NOTIFICATION_PAGES)) {
    if (config.menuKeys.includes(menuKey)) return key;
    if (config.menuTitles.includes(title)) return key;
  }
  return null;
}

/**
 * Client-side safety filter so cross-module items never appear
 * even if the API returns mixed results.
 */
export function filterNotificationsForModule(notifications, pathname) {
  const key = getNotificationModuleKey(pathname);
  if (!key || !Array.isArray(notifications)) return [];

  const tableName = MODULE_NOTIFICATION_PAGES[key].tableName;

  return notifications.filter((notif) => {
    if (notif?.table_name && notif.table_name === tableName) return true;

    const type = Number(notif?.type);
    if (key === 'tasks') {
      return (
        TASK_TYPES.has(type) ||
        PROJECT_TYPES.has(type) ||
        notif?.table_name === 'Projects' ||
        Boolean(notif?.task || notif?.task_id || notif?.taskid || notif?.project)
      );
    }
    if (key === 'forum') {
      return FORUM_TYPES.has(type) || notif?.table_name === 'DiscussionForums';
    }
    if (key === 'documents') {
      return DOCUMENT_TYPES.has(type) || notif?.table_name === 'Documents';
    }
    return false;
  });
}

/** Keep notifications created within the last `maxAgeDays` (for header dropdown only). */
export function filterNotificationsByMaxAge(
  notifications,
  maxAgeDays = DROPDOWN_NOTIFICATION_MAX_AGE_DAYS
) {
  if (!Array.isArray(notifications)) return [];
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  return notifications.filter((notif) => {
    if (!notif?.created_at) return false;
    const created = new Date(notif.created_at).getTime();
    if (Number.isNaN(created)) return false;
    return created >= cutoff;
  });
}

export function notifyNotificationsUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT));
  }
}
